import { ChangeEvent, useState } from 'react'

import { IconPlus, IconEdit, IconTrash } from '@tabler/icons-react'
import { Checkbox, ConfigProvider, Input, List, Typography } from 'antd'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import styled from 'styled-components'

import { Button } from '../shared-components/BUI/Button'

import { TodoCaptureBlock } from '@/modules/templates/types/blueprint'

const ListItem = styled(List.Item)`
  padding-left: 0 !important;
  padding-right: 0 !important;
`

const ListItemMeta = styled(List.Item.Meta)`
  & > .ant-list-item-meta-avatar {
    align-self: stretch;
    display: flex;
    align-items: center;
  }
`

const Text = styled(Typography.Text)<{ completed?: boolean }>`
  text-decoration: ${({ completed }) => (completed ? 'line-through' : 'none')};
`

const IconButton = styled(Button).attrs({ type: 'button', size: 'small' })`
  color: ${({ theme }) => theme.colour.secondary._400};
  border: 0;
`

type Value = TodoCaptureBlock['todos']

type Props = {
  value?: Value
  onChange?: (value: Value) => void
}

export const Todo: React.FC<Props> = ({ value = [], onChange }) => {
  const [editingIndex, setEditingIndex] = useState<number>(null)

  const addTodo = () => {
    const newValue = [...value, { label: '', complete: false }]

    onChange?.(newValue)
    setEditingIndex(value.length)
  }

  const edit = (index: number) => {
    setEditingIndex((currentIndex) => (currentIndex === index ? null : index))
  }

  const update = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = [...value]
    newValue[index] = { ...newValue[index], label: e.target.value }
    onChange?.(newValue)
  }

  const deleteTodo = (index: number) => {
    const newValue = [...value]
    newValue.splice(index, 1)
    onChange?.(newValue)
  }

  const complete = (index: number) => (e: CheckboxChangeEvent) => {
    const newValue = [...value]
    newValue[index] = { ...newValue[index], complete: e.target.checked }
    onChange?.(newValue)
  }

  return (
    <ConfigProvider renderEmpty={() => "No todo's yet"}>
      <List
        size="small"
        dataSource={value}
        renderItem={(todo, index) => (
          <ListItem>
            <ListItemMeta
              avatar={
                <Checkbox
                  defaultChecked={todo.complete}
                  onChange={complete(index)}
                />
              }
              description={
                index === editingIndex ? (
                  <Input
                    autoFocus
                    defaultValue={todo.label}
                    onBlur={() => edit(null)}
                    onChange={update(index)}
                  />
                ) : (
                  <>
                    {todo.label && (
                      <Text completed={todo.complete}>{todo.label}</Text>
                    )}

                    <IconButton
                      icon={<IconEdit size={16} />}
                      onClick={() => edit(index)}
                    />
                  </>
                )
              }
            />
            <IconButton
              icon={<IconTrash size={16} />}
              onClick={() => deleteTodo(index)}
              negative
            />
          </ListItem>
        )}
      />
      <br />
      <Button onClick={addTodo} icon={<IconPlus size={16} />}>
        Add todo
      </Button>
    </ConfigProvider>
  )
}
